import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const LakeCowichan = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.lake-cowichan:About Lake Cowichan Paragraph 1">
          Lake Cowichan, nestled on the eastern shores of Cowichan Lake on
          Vancouver Island in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Canada, is a haven of natural beauty and outdoor adventure.
          Surrounded by lush forests and rolling hills, the town offers a
          picturesque setting for a range of recreational activities. Cowichan
          Lake is a focal point, inviting swimming, boating, kayaking, and
          fishing enthusiasts, while the Cowichan Valley Trail provides scenic
          display at the Kaatza Station Museum. Lake Cowichan’s close-knit
          community celebrates its heritage through events like the Lake Days
          Festival. With essential amenities and proximity to the Cowichan
          Valley’s wineries and parks, it’s a gateway to outdoor adventures in
          the diverse landscape of{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $2,000',
          bed2: '$1,200 - $2,500',
          bed3: '$2,000 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.lake-cowichan:Apartment Hunting in Lake Cowichan Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Lake Cowichan
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Apartments for rent Lake Cowichan Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Lake Cowichan
              </LinkText>{' '}
              typically attract individuals, couples, and small families seeking
              a simple, convenient lifestyle. These units, which range from
              older, more characterful buildings to modern developments, offer
              residents the benefit of being close to local amenities like
              shops, and the natural beauty of the lake and surrounding areas.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Houses for rent Lake Cowichan Paragraph">
              Renting a house in Lake Cowichan is popular among families and
              those who appreciate a more spacious and private living
              environment.{' '}
              <LinkText href={links.house}>
                Houses for rent in Lake Cowichan
              </LinkText>{' '}
              often come with yards, providing a perfect setting for outdoor
              activities and relaxation amidst the town’s tranquil surroundings.
              The houses in Lake Cowichan vary in style, from older, rustic
              homes to newer, modern designs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Townhouses for rent Lake Cowichan Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Lake Cowichan
              </LinkText>{' '}
              offer a balance of community and privacy. Preferred by small
              families and couples, these units usually provide multiple levels
              of living space and may include small private outdoor areas.
              Townhouses are often part of smaller communities, offering a sense
              of neighborhood within the peaceful setting of Lake Cowichan.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Basements for rent Lake Cowichan Paragraph">
              Basements for rent in Lake Cowichan provide a more affordable
              living option. These lower-level units, generally part of larger
              homes, are a practical choice for singles, students, or those
              seeking budget-friendly accommodation. While offering less natural
              light, they provide a cozy living space in a scenic environment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Condos for rent Lake Cowichan Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Lake Cowichan
              </LinkText>{' '}
              are ideal for those looking for an easy-to-maintain living space
              with a sense of community. These units vary from modest to more
              upscale and are particularly appealing to retirees, young
              professionals, or small families. Condos in Lake Cowichan often
              take advantage of the town’s natural beauty, with some providing
              views of the lake or the forests.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Rooms for rent Lake Cowichan Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Lake Cowichan
              </LinkText>{' '}
              is the most cost-effective housing option, typically chosen by
              students, seasonal workers, or individuals seeking flexible living
              arrangements. This type of rental involves sharing common
              facilities like kitchens and bathrooms, offering a communal living
              experience in a friendly, small-town atmosphere.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Short term rentals Lake Cowichan Paragraph">
              Lake Cowichan, a small town situated on Vancouver Island in
              British Columbia, offers some employment opportunities,
              particularly in sectors like tourism, retail, and forestry, which
              are reflective of its size and regional economy. It is not
              prominently known for its academic institutions, being more of a
              small, community-focused town rather than an educational center.
              The demand for{' '}
              <LinkText href={links.shortTerm}>
                short-term rentals in Lake Cowichan
              </LinkText>{' '}
              is noticeable, especially during the summer months when the area
              becomes a popular destination for its outdoor recreational
              activities and scenic lake. This seasonal pattern of short-term
              rental demand is different from larger cities or university towns,
              where such rentals are popular year-round, peaking during summer
              and at the start of school semesters.{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                The flexibility of short-term rentals is an appealing feature
                for many
              </LinkText>
              , especially for those seeking accommodation for holiday stays or
              temporary work in the area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.lake-cowichan:Cheap rentals Lake Cowichan Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Lake Cowichan
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Lake Cowichan, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.lake-cowichan:Living in Lake Cowichan Paragraph">
            Nestled on the western shore of Cowichan Lake, Lake Cowichan, BC is
            a hidden paradise that combines the beauty of nature with the
            tranquility of small-town living. Surrounded by majestic mountains,
            lush forests, and the crystal-clear waters of the lake, this idyllic
            town is a haven for outdoor enthusiasts. Whether you enjoy hiking
            through the picturesque trails, fishing in the pristine waters, or
            simply basking in the serenity of nature, Lake Cowichan offers it
            all. But it’s not just the natural beauty that sets Lake Cowichan
            apart. The close-knit community and warm hospitality create a sense
            of belonging and make it easy to forge lifelong connections. With a
            rich history, vibrant arts scene, and engaging local events, there’s
            always something to explore and discover in Lake Cowichan. And when
            you need a break from the fast-paced world, the peaceful ambiance
            and relaxed lifestyle provide the perfect escape. Whether you’re a
            resident or a visitor, Lake Cowichan is a place where you can truly
            embrace lakeside living and immerse yourself in the magic of
            small-town charm.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.lake-cowichan:Living in Lake Cowichan Paragraph 2">
            If you’re not sure that Lake Cowichan is the right city for you,
            check out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.lake-cowichan:Popular Neighbourhoods Lake Cowichan Paragraph">
          Like any city, Lake Cowichan is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Downtown Lake Cowichan, Waterfront, Cowichan Lake Road, North
          Shore Road, and South Shore Road. For more info about Lake Cowichan,
          you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.lake-cowichan:Local Landmarks Lake Cowichan Paragraph">
          Lake Cowichan, BC, is home to several captivating landmarks that
          showcase the area’s natural beauty and rich heritage. One such
          landmark is Cowichan River, a stunning waterway that offers
          breathtaking views and endless recreational opportunities. Visitors
          can indulge in activities such as fishing, kayaking, and swimming,
          surrounded by the serene beauty of the river. Another notable landmark
          is the Kaatza Station Museum, which houses a remarkable collection of
          artifacts that depict the history and culture of the region. The
          museum provides visitors with a fascinating glimpse into the area’s
          past and its significance in shaping the community. Additionally, the
          Cowichan Valley Trail is a must-visit landmark for outdoor
          enthusiasts. This picturesque trail spans through lush forests and
          scenic landscapes, offering hikers and cyclists a chance to explore
          the area’s natural wonders. These landmarks make Lake Cowichan, BC, an
          enchanting destination that celebrates its natural splendor and
          cultural legacy.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default LakeCowichan
